import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Inbox, RefreshCw } from "lucide-react";
import { tokenStore, formatApiErrorDetail } from "../../lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function authedRequest(path) {
  const token = tokenStore.get();
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (_) {
    return { ok: false, error: "Network error" };
  }
  if (res.status === 401) {
    tokenStore.clear();
    if (typeof window !== "undefined") window.location.assign("/admin/login");
    return { ok: false, status: 401, error: "Unauthorized" };
  }
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) {
    return { ok: false, status: res.status, error: formatApiErrorDetail(data?.detail) || `Request failed (${res.status})` };
  }
  return { ok: true, data };
}

const PAGE_SIZE = 25;

export default function AdminChatSessions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = async (p = page) => {
    setLoading(true);
    setError(null);
    const res = await authedRequest(`/admin/chat-sessions?page=${p}&page_size=${PAGE_SIZE}`);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setItems(res.data.items || []);
    setTotal(res.data.total || 0);
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const openSession = async (session_id) => {
    setActiveId(session_id);
    setActiveSession(null);
    setActiveLoading(true);
    const res = await authedRequest(`/admin/chat-sessions/${encodeURIComponent(session_id)}`);
    setActiveLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setActiveSession(res.data);
  };

  const closeDrawer = () => {
    setActiveId(null);
    setActiveSession(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-chat-sessions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="overline">Chat</span>
          <h1 className="h2 mt-2">Chat Sessions</h1>
          <p className="body-base mt-2 text-ridge-muted">
            All conversations with the Ridgehawk virtual assistant.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(page)}
          className="inline-flex items-center gap-2 rounded-md border border-ridge-border bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal hover:bg-ridge-stone"
          data-testid="chat-sessions-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {error ? (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="chat-sessions-error">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md border border-ridge-border bg-white">
        <div className="grid grid-cols-12 gap-4 border-b border-ridge-border bg-ridge-stone px-4 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">
          <div className="col-span-3">Session</div>
          <div className="col-span-5">Last message</div>
          <div className="col-span-2">Messages</div>
          <div className="col-span-2 text-right">Updated</div>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-sm text-ridge-muted" data-testid="chat-sessions-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-sm text-ridge-muted" data-testid="chat-sessions-empty">
            <Inbox className="h-6 w-6" />
            No chat sessions yet.
          </div>
        ) : (
          items.map((s) => (
            <button
              key={s.session_id}
              type="button"
              onClick={() => openSession(s.session_id)}
              className="grid w-full grid-cols-12 items-center gap-4 border-b border-ridge-border px-4 py-3 text-left text-sm transition-colors hover:bg-ridge-stone/60"
              data-testid={`chat-session-row-${s.session_id}`}
            >
              <div className="col-span-3 truncate">
                <div className="font-mono text-xs text-ridge-charcoal">{s.session_id.slice(0, 24)}…</div>
                <div className="mt-0.5 text-[0.65rem] uppercase tracking-[0.15em] text-ridge-muted">
                  {s.lead_id ? "Linked to lead" : "Anonymous"}
                </div>
              </div>
              <div className="col-span-5 truncate text-ridge-text">
                <span className="mr-2 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ridge-amber">
                  {s.last_role || "—"}
                </span>
                {s.last_preview || <span className="text-ridge-muted">(no messages)</span>}
              </div>
              <div className="col-span-2 text-ridge-text">{s.message_count}</div>
              <div className="col-span-2 text-right text-ridge-muted">
                {fmt(s.updated_at)}
              </div>
            </button>
          ))
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-ridge-muted">
        <span>{total} session{total === 1 ? "" : "s"} total</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md border border-ridge-border bg-white px-3 py-1.5 disabled:opacity-50"
            data-testid="chat-sessions-prev"
          >
            Prev
          </button>
          <span data-testid="chat-sessions-pageinfo">{page} / {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-md border border-ridge-border bg-white px-3 py-1.5 disabled:opacity-50"
            data-testid="chat-sessions-next"
          >
            Next
          </button>
        </div>
      </div>

      {/* Drawer */}
      {activeId ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-40"
          data-testid="chat-session-drawer"
        >
          <button
            type="button"
            aria-label="Close drawer"
            onClick={closeDrawer}
            className="absolute inset-0 bg-black/40"
          />
          <div className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-ridge-border bg-ridge-charcoal px-5 py-4 text-white">
              <div className="min-w-0">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white/70">Session</p>
                <p className="truncate font-mono text-xs">{activeId}</p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                data-testid="chat-session-drawer-close"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-ridge-stone/50 px-5 py-5">
              {activeLoading ? (
                <p className="text-sm text-ridge-muted">Loading…</p>
              ) : activeSession ? (
                <div className="space-y-4">
                  <SessionMeta s={activeSession} />
                  <div className="space-y-3">
                    {(activeSession.messages || []).map((m, i) => (
                      <SessionBubble key={i} role={m.role} content={m.content} ts={m.ts} />
                    ))}
                    {(activeSession.messages || []).length === 0 ? (
                      <p className="text-sm text-ridge-muted">No messages.</p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-ridge-muted">Could not load session.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SessionMeta({ s }) {
  const ua = s.user_meta?.user_agent || "";
  const referrer = s.user_meta?.referrer || "";
  return (
    <div className="rounded-md border border-ridge-border bg-white p-4 text-xs text-ridge-text">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Meta label="Created" value={fmt(s.created_at)} />
        <Meta label="Updated" value={fmt(s.updated_at)} />
        <Meta label="Lead" value={s.lead_id || "—"} />
        <Meta label="Messages" value={(s.messages || []).length} />
        <Meta label="Referrer" value={referrer || "—"} />
        <Meta label="User-Agent" value={ua ? ua.slice(0, 80) : "—"} />
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">{label}</div>
      <div className="mt-0.5 break-all">{value}</div>
    </div>
  );
}

function SessionBubble({ role, content, ts }) {
  const mine = role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`session-bubble-${role}`}>
      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
        mine ? "bg-ridge-charcoal text-white" : "border border-ridge-border bg-white text-ridge-text"
      }`}>
        <div className="mb-1 flex items-center gap-2 text-[0.6rem] font-semibold uppercase tracking-[0.18em] opacity-70">
          {role === "user" ? "User" : "Assistant"}
          <span>· {fmt(ts)}</span>
        </div>
        {mine ? (
          <p className="whitespace-pre-wrap break-words">{content}</p>
        ) : (
          <div className="prose-ridge break-words">
            <ReactMarkdown>{content || ""}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString();
  } catch {
    return s;
  }
}
